import { useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogContent, DialogTitle, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Tab, Tabs, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDataProvider, useNotify, useRefresh } from "react-admin";
import { useQuery } from "react-query";

const emptyFilter = { brand_codes: "", size_codes: "", motif_codes: "", symbols: "" };
const emptyGlobal = { adjustment_type: "amount", buy_price_adjustment: "", sell_price_adjustment: "" };
const readableError = (error) => error?.body?.message || error?.message || "Proses bulk harga gagal";

export default function BulkPriceRolloverDialog({ open, setOpen }) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [tab, setTab] = useState(0);
  const [filter, setFilter] = useState(emptyFilter);
  const [dates, setDates] = useState({ start_date: "", end_date: "" });
  const [globalAdjustment, setGlobalAdjustment] = useState(emptyGlobal);
  const [adjustments, setAdjustments] = useState([]);
  const [request, setRequest] = useState(null);
  const [preview, setPreview] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [distributionMode, setDistributionMode] = useState("none");
  const [selectedCompanyIDs, setSelectedCompanyIDs] = useState([]);
  const [previewPage, setPreviewPage] = useState(0);
  const previewRows = preview?.rows || [];
  const visibleRows = useMemo(() => previewRows.slice(previewPage * 10, previewPage * 10 + 10), [previewRows, previewPage]);
  const { data: companies = [] } = useQuery(
    ["companies/select", "bulk-price"],
    () => dataProvider.getListNoParams("companies/select")
  );
  const depotCompanies = useMemo(() => companies.filter((company) => {
    const label = `${company.alias || ""} ${company.name || ""}`.toLowerCase();
    return !label.includes("head office") && !label.includes("head-office") && label.trim() !== "ho";
  }), [companies]);

  const updateFilter = (field) => (event) => {
    setFilter((current) => ({ ...current, [field]: event.target.value }));
    setAdjustments([]);
    setPreview(null);
  };
  const updateAdjustment = (index, field, value) => setAdjustments((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item));

  const loadMotifs = async () => {
    setLoading(true);
    try {
      if (distributionMode === "selected" && !selectedCompanyIDs.length) {
        notify("Pilih minimal satu depo target.", { type: "warning" });
        return;
      }
      const result = await dataProvider.simplePostJson("prices/bulk-rollover/motifs", { data: {
        ...filter,
        distribution_mode: distributionMode,
        target_company_ids: selectedCompanyIDs,
      } });
      setAdjustments((result.motifs || []).map((motif) => ({ motif, ...emptyGlobal })));
      setPreview(null);
      setTab(1);
      if (!result.count) notify("Tidak ada motif yang cocok dengan filter.", { type: "warning" });
    } catch (error) {
      notify(readableError(error), { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const applyToAll = () => {
    setAdjustments((current) => current.map((item) => ({ ...item, ...globalAdjustment })));
    setPreview(null);
  };

  const buildRequest = () => ({
    ...filter, ...dates, adjustments,
    distribution_mode: distributionMode,
    target_company_ids: selectedCompanyIDs,
  });
  const showPreview = async () => {
    if (!dates.start_date || !dates.end_date) {
      notify("Tanggal mulai dan tanggal akhir wajib diisi.", { type: "warning" });
      setTab(0);
      return;
    }
    setLoading(true);
    try {
      const payload = buildRequest();
      const result = await dataProvider.simplePostJson("prices/bulk-rollover/preview", { data: payload });
      setRequest(payload);
      setPreview(result);
      setPreviewPage(0);
    } catch (error) {
      notify(readableError(error), { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    if (!request || !preview?.count) return;
    setLoading(true);
    try {
      const result = await dataProvider.simplePostJson("prices/bulk-rollover", { data: request });
      const failedCompanies = (result.companies || []).filter((company) => company.status === "failed");
      setExecutionResult(result);
      notify(failedCompanies.length
        ? `${result.processed} harga masuk antrean, tetapi ${failedCompanies.length} depo gagal dibuatkan antrean`
        : distributionMode === "none"
          ? `${result.processed} harga berhasil dibuat`
          : `${result.processed} harga masuk antrean dalam ${result.group_count || 0} grup sync`, { type: failedCompanies.length ? "warning" : "success" });
      if (!failedCompanies.length) setOpen(false);
      refresh();
    } catch (error) {
      notify(readableError(error), { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: "94vh" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1 }}>
        <DialogTitle>Bulk Update Harga Efektif</DialogTitle>
        <IconButton onClick={() => setOpen(false)} disabled={loading}><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Filter dapat diisi beberapa nilai dengan pemisah koma. Sistem menutup harga lama sehari sebelum periode baru.
        </Typography>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
          <Tab label="1. Filter & Periode" />
          <Tab label={`2. Aturan Motif (${adjustments.length})`} disabled={!adjustments.length} />
        </Tabs>

        {tab === 0 ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
            <TextField label="Brand" value={filter.brand_codes} onChange={updateFilter("brand_codes")} placeholder="Contoh: G,M,S" />
            <TextField label="Ukuran" value={filter.size_codes} onChange={updateFilter("size_codes")} placeholder="Contoh: 36,40,60" />
            <TextField label="Motif" value={filter.motif_codes} onChange={updateFilter("motif_codes")} placeholder="Contoh: VSA,BEV,ZIM" />
            <TextField label="Simbol karakter ke-7" value={filter.symbols} onChange={updateFilter("symbols")} placeholder="Contoh: #,^,+" />
            <TextField label="Tanggal Mulai Efektif" type="date" value={dates.start_date} onChange={(event) => setDates((current) => ({ ...current, start_date: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Tanggal Akhir Efektif" type="date" value={dates.end_date} onChange={(event) => setDates((current) => ({ ...current, end_date: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <FormControl fullWidth>
              <InputLabel id="bulk-distribution-label">Distribusi</InputLabel>
              <Select labelId="bulk-distribution-label" label="Distribusi" value={distributionMode} onChange={(event) => {
                setDistributionMode(event.target.value);
                setAdjustments([]);
                setPreview(null);
              }}>
                <MenuItem value="none">Tidak didistribusikan</MenuItem>
                <MenuItem value="all">Semua depo</MenuItem>
                <MenuItem value="selected">Depo tertentu</MenuItem>
              </Select>
            </FormControl>
            {distributionMode === "selected" ? (
              <Autocomplete multiple options={depotCompanies}
                value={depotCompanies.filter((company) => selectedCompanyIDs.includes(company.id))}
                onChange={(_, values) => { setSelectedCompanyIDs(values.map((value) => value.id)); setAdjustments([]); setPreview(null); }}
                getOptionLabel={(company) => company.alias || company.name || company.id}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => <TextField {...params} label="Depo target" placeholder="Pilih satu atau beberapa depo" />}
              />
            ) : <Box />}
            {distributionMode !== "none" ? (
              <Alert severity="info" sx={{ gridColumn: "1 / -1" }}>
                Harga dihitung dari harga aktif terakhir masing-masing depo. Pengiriman dipotong maksimal 500 harga per grup dan akan dicoba ulang jika depo sedang offline.
              </Alert>
            ) : null}
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Button variant="outlined" onClick={loadMotifs} disabled={loading}>Muat Daftar Motif</Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Isi nilai yang sama untuk semua motif</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" }, gap: 2 }}>
                <FormControl><InputLabel>Jenis Perubahan</InputLabel><Select label="Jenis Perubahan" value={globalAdjustment.adjustment_type} onChange={(event) => setGlobalAdjustment((current) => ({ ...current, adjustment_type: event.target.value }))}><MenuItem value="amount">Nominal (Rp)</MenuItem><MenuItem value="percent">Persentase (%)</MenuItem></Select></FormControl>
                <TextField label="Perubahan Harga Beli" helperText="Gunakan minus untuk mengurangi" value={globalAdjustment.buy_price_adjustment} onChange={(event) => setGlobalAdjustment((current) => ({ ...current, buy_price_adjustment: event.target.value }))} />
                <TextField label="Perubahan Harga Jual" helperText="Gunakan minus untuk mengurangi" value={globalAdjustment.sell_price_adjustment} onChange={(event) => setGlobalAdjustment((current) => ({ ...current, sell_price_adjustment: event.target.value }))} />
                <Button variant="outlined" onClick={applyToAll}>Terapkan ke Semua</Button>
              </Box>
            </Paper>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Motif</TableCell><TableCell>Jenis</TableCell><TableCell>Perubahan Harga Beli</TableCell><TableCell>Perubahan Harga Jual</TableCell></TableRow></TableHead>
                <TableBody>{adjustments.map((item, index) => (
                  <TableRow key={item.motif}>
                    <TableCell>{item.motif}</TableCell>
                    <TableCell><Select size="small" value={item.adjustment_type} onChange={(event) => updateAdjustment(index, "adjustment_type", event.target.value)}><MenuItem value="amount">Nominal</MenuItem><MenuItem value="percent">Persentase</MenuItem></Select></TableCell>
                    <TableCell><TextField size="small" value={item.buy_price_adjustment} onChange={(event) => updateAdjustment(index, "buy_price_adjustment", event.target.value)} /></TableCell>
                    <TableCell><TextField size="small" value={item.sell_price_adjustment} onChange={(event) => updateAdjustment(index, "sell_price_adjustment", event.target.value)} /></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
            <Button variant="contained" sx={{ mt: 2 }} onClick={showPreview} disabled={loading}>Tampilkan Preview</Button>
          </Box>
        )}

        {preview ? (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Preview: {preview.count} harga pada {preview.company_count || 1} company akan diproses</Typography>
            {preview.companies?.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {preview.companies.map((company) => `${company.company_name || company.company_id}: ${company.price_count} harga / ${company.group_count} grup`).join(" | ")}
              </Typography>
            ) : null}
            {preview.rows_truncated ? <Alert severity="info" sx={{ mb: 1 }}>Tabel hanya menampilkan 500 baris pertama. Semua {preview.count} harga tetap akan diproses.</Alert> : null}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 330 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>Kode</TableCell><TableCell>KW</TableCell><TableCell>Harga Beli Lama → Baru</TableCell><TableCell>Harga Jual Lama → Baru</TableCell><TableCell>Periode Baru</TableCell></TableRow></TableHead>
                <TableBody>{visibleRows.map((row) => <TableRow key={row.id}><TableCell>{row.code}</TableCell><TableCell>{row.quality_code || "-"}</TableCell><TableCell>{row.old_buy_price} → {row.new_buy_price}</TableCell><TableCell>{row.old_sell_price} → {row.new_sell_price}</TableCell><TableCell>{row.new_start_date} s/d {row.new_end_date}</TableCell></TableRow>)}</TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={previewRows.length} page={previewPage} onPageChange={(_, page) => setPreviewPage(page)} rowsPerPage={10} rowsPerPageOptions={[10]} labelRowsPerPage="Baris per halaman" />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}><Button variant="contained" color="primary" onClick={execute} disabled={loading || !preview.count}>Konfirmasi & Proses</Button></Box>
          </Box>
        ) : null}
        {executionResult?.companies?.some((company) => company.status === "failed") ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {executionResult.companies.filter((company) => company.status === "failed").map((company) => `${company.company_name || company.company_id}: ${company.message}`).join(" | ")}
          </Alert>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
